import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Plus, X } from 'lucide-react';
import { POLL_LIMITS, pollContentSchema } from '@twinden/shared';
import {
  cleanOptions,
  closesInSeconds,
  type CloseAfter,
} from '../../lib/polls';
import type { PollDraft } from '../../store';
import { useT } from '../../i18n';

// "+ → Poll" (M60, D220): a question, 2–10 options, one or several answers,
// and when it closes. It says up front that votes aren't private: they're
// metadata the server keeps (D54).

const CLOSE_CHOICES: CloseAfter[] = ['never', 'hour', 'day', 'week'];

export function PollSheet({
  isOpen,
  onClose,
  onSend,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSend: (question: string, poll: PollDraft) => void;
}) {
  const t = useT();
  const phone = useBreakpointValue({ base: true, md: false }) ?? true;
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multiple, setMultiple] = useState(false);
  const [closeAfter, setCloseAfter] = useState<CloseAfter>('never');

  // A fresh poll each time the sheet opens.
  useEffect(() => {
    if (!isOpen) return;
    setQuestion('');
    setOptions(['', '']);
    setMultiple(false);
    setCloseAfter('never');
  }, [isOpen]);

  const draft = { question: question.trim(), options: cleanOptions(options) };
  const valid = pollContentSchema.safeParse(draft).success;

  const send = () => {
    if (!valid) return;
    onSend(draft.question, {
      options: draft.options,
      multiple,
      closesInSeconds: closesInSeconds(closeAfter),
    });
  };

  const content = (
    <Stack spacing={4}>
      <Heading as="h2" fontSize="24px" fontWeight={700}>
        {t('chat.poll.title')}
      </Heading>
      <FormControl>
        <FormLabel fontWeight={700}>{t('chat.poll.question')}</FormLabel>
        <Input
          value={question}
          maxLength={POLL_LIMITS.QUESTION_LENGTH}
          placeholder={t('chat.poll.questionPlaceholder')}
          onChange={(event) => setQuestion(event.target.value)}
          autoFocus
        />
      </FormControl>
      <Box>
        <Text fontWeight={700} mb={2}>
          {t('chat.poll.options')}
        </Text>
        <Stack spacing={2}>
          {options.map((option, index) => (
            <HStack key={index} spacing={2}>
              <Input
                value={option}
                maxLength={POLL_LIMITS.OPTION_LENGTH}
                placeholder={t('chat.poll.option', { n: index + 1 })}
                aria-label={t('chat.poll.option', { n: index + 1 })}
                onChange={(event) =>
                  setOptions((list) =>
                    list.map((value, i) =>
                      i === index ? event.target.value : value
                    )
                  )
                }
              />
              {options.length > POLL_LIMITS.MIN_OPTIONS && (
                <IconButton
                  aria-label={t('chat.poll.removeOption', { n: index + 1 })}
                  icon={<X size={16} />}
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() =>
                    setOptions((list) => list.filter((_, i) => i !== index))
                  }
                />
              )}
            </HStack>
          ))}
        </Stack>
        {options.length < POLL_LIMITS.MAX_OPTIONS && (
          <Button
            mt={2}
            size="sm"
            variant="ghost"
            leftIcon={<Plus size={16} />}
            onClick={() => setOptions((list) => [...list, ''])}
          >
            {t('chat.poll.addOption')}
          </Button>
        )}
      </Box>
      <FormControl display="flex" alignItems="center" gap={3}>
        <Switch
          id="poll-multiple"
          isChecked={multiple}
          onChange={(event) => setMultiple(event.target.checked)}
        />
        <FormLabel htmlFor="poll-multiple" mb={0} fontWeight={600}>
          {t('chat.poll.multiple')}
        </FormLabel>
      </FormControl>
      <Box>
        <Text fontWeight={700} mb={2}>
          {t('chat.poll.closeAfter')}
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          {CLOSE_CHOICES.map((choice) => (
            <Button
              key={choice}
              size="sm"
              borderRadius="full"
              variant={closeAfter === choice ? 'solid' : 'outline'}
              colorScheme={closeAfter === choice ? 'brand' : 'gray'}
              aria-pressed={closeAfter === choice}
              onClick={() => setCloseAfter(choice)}
            >
              {t(`chat.poll.${choice}`)}
            </Button>
          ))}
        </HStack>
      </Box>
      <Text fontSize="13px" color="muted">
        {t('chat.poll.visibleVotes')}
      </Text>
      <HStack spacing={3}>
        <Button
          flex="1"
          h="52px"
          variant="outline"
          colorScheme="gray"
          borderColor="line"
          onClick={onClose}
        >
          {t('common.cancel')}
        </Button>
        <Button
          flex="1"
          h="52px"
          colorScheme="brand"
          isDisabled={!valid}
          onClick={send}
        >
          {t('chat.poll.create')}
        </Button>
      </HStack>
    </Stack>
  );

  if (phone) {
    return (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
        <DrawerOverlay bg="rgba(31, 29, 27, 0.5)" />
        <DrawerContent
          borderTopRadius="28px"
          bg="surface"
          aria-label={t('chat.poll.title')}
          maxH="92dvh"
          pb="max(env(safe-area-inset-bottom), 20px)"
        >
          <DrawerBody px={5} pt="10px" pb={0} overflowY="auto">
            <Box
              w="40px"
              h="5px"
              borderRadius="3px"
              bg="line"
              mx="auto"
              mb={4}
              aria-hidden="true"
            />
            {content}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(31, 29, 27, 0.5)" />
      <ModalContent
        mx={5}
        borderRadius="28px"
        bg="surface"
        aria-label={t('chat.poll.title')}
      >
        <ModalBody px={5} py={6}>
          {content}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
